// Telefone com DDI — espelho do PhoneNumber do backend (Application/Common/Phones).
// O legado não tinha DDI: DDI vazio significa Brasil (55). No Brasil o DDD fica em campo
// próprio; fora dele não há DDD e o número nacional vai inteiro no número.

export const BRAZIL_DDI = "55";

/** Telefone como os formulários o editam: só dígitos em cada parte. */
export interface PhoneValue {
  ddi: string;
  ddd: string;
  number: string;
}

export const EMPTY_PHONE: PhoneValue = { ddi: BRAZIL_DDI, ddd: "", number: "" };

export const onlyDigits = (value?: string | null): string => (value ?? "").replace(/\D/g, "");

/**
 * Telefone de vínculo (UEPR) migrado como texto de decimal ("987045171.000000"): só a parte
 * inteira é o número — a mesma leitura do InvitePhone do servidor.
 */
export const legacyPhonePart = (value?: string | null): string => onlyDigits((value ?? "").split(/[.,]/)[0]);

// Códigos de 2 dígitos da UIT; os de 1 dígito são 1 e 7; o resto tem 3 (plano livre de prefixo).
const TWO_DIGIT_CODES = new Set([
  "20", "27", "30", "31", "32", "33", "34", "36", "39", "40", "41", "43", "44", "45", "46", "47", "48", "49",
  "51", "52", "53", "54", "55", "56", "57", "58", "60", "61", "62", "63", "64", "65", "66",
  "81", "82", "84", "86", "90", "91", "92", "93", "94", "95", "98",
]);

const callingCodeOf = (digits: string): string | null => {
  if (digits.length < 2) return null;
  if (digits[0] === "1" || digits[0] === "7") return digits.slice(0, 1);
  if (TWO_DIGIT_CODES.has(digits.slice(0, 2))) return digits.slice(0, 2);
  return digits.length < 4 ? null : digits.slice(0, 3);
};

const ddiOrBrazil = (ddi?: string | null): string => onlyDigits(ddi) || BRAZIL_DDI;

// Brasil: o DDD só entra quando o número ainda não traz um (o legado gravou dos dois jeitos).
// Fora do Brasil o 0 inicial é prefixo de tronco e não vai no internacional (080… no Japão = +81 80…),
// exceto Itália/Vaticano (39) e San Marino (378). Espelha o PhoneNumber do backend.
const KEEPS_LEADING_ZERO = new Set(["39", "378"]);
const withoutTrunkZero = (code: string, national: string): string =>
  national.startsWith("0") && !KEEPS_LEADING_ZERO.has(code) ? national.slice(1) : national;

const nationalDigits = (code: string, ddd?: string | null, number?: string | null): string => {
  const n = onlyDigits(number);
  if (!n) return "";
  if (code !== BRAZIL_DDI) return withoutTrunkZero(code, onlyDigits(ddd) + n);
  return n.length >= 10 ? n : onlyDigits(ddd) + n;
};

const stripTypedBrazilDdi = (national: string): string =>
  (national.length === 12 || national.length === 13) && national.startsWith(BRAZIL_DDI) ? national.slice(2) : national;

// Grupos de 3 da esquerda; um dígito que sobraria sozinho junta-se ao último grupo.
const group = (digits: string): string => {
  const parts: string[] = [];
  let i = 0;
  while (i < digits.length) {
    const remaining = digits.length - i;
    const size = remaining === 4 ? 4 : Math.min(3, remaining);
    parts.push(digits.slice(i, i + size));
    i += size;
  }
  return parts.join(" ");
};

/** "+55 (11) 98765-4321", "+55 (11) 3456-7890" ou "+351 912 345 678". Nulo sem número. */
export const formatPhoneDisplay = (ddi?: string | null, ddd?: string | null, number?: string | null): string | null => {
  const code = ddiOrBrazil(ddi);
  let national = nationalDigits(code, ddd, number);
  if (!national) return null;
  if (code !== BRAZIL_DDI) return `+${code} ${group(national)}`;
  national = stripTypedBrazilDdi(national);
  switch (national.length) {
    case 11: return `+55 (${national.slice(0, 2)}) ${national.slice(2, 7)}-${national.slice(7)}`;
    case 10: return `+55 (${national.slice(0, 2)}) ${national.slice(2, 6)}-${national.slice(6)}`;
    case 9: return `+55 ${national.slice(0, 5)}-${national.slice(5)}`;
    case 8: return `+55 ${national.slice(0, 4)}-${national.slice(4)}`;
    default: return `+55 ${national}`;
  }
};

/** DDI + número só com dígitos, pronto para o wa.me. Nulo sem número plausível. */
export const toE164Digits = (ddi?: string | null, ddd?: string | null, number?: string | null): string | null => {
  const code = ddiOrBrazil(ddi);
  const national = nationalDigits(code, ddd, number);
  if (code !== BRAZIL_DDI) return national.length < 6 ? null : code + national;
  if ((national.length === 12 || national.length === 13) && national.startsWith(BRAZIL_DDI)) return national;
  return national.length < 10 ? null : BRAZIL_DDI + national;
};

/** Exibição a partir dos dígitos E.164 que a API devolve para o wa.me. */
export const formatE164Display = (digits?: string | null): string | null => {
  const d = onlyDigits(digits);
  const code = callingCodeOf(d);
  return code ? formatPhoneDisplay(code, null, d.slice(code.length)) : null;
};

/** Link do WhatsApp; nulo sem número. */
export const whatsAppLink = (e164Digits: string | null | undefined, text?: string): string | null => {
  const d = onlyDigits(e164Digits);
  if (!d) return null;
  return text ? `https://wa.me/${d}?text=${encodeURIComponent(text)}` : `https://wa.me/${d}`;
};

/** Partes gravadas → valor do formulário (DDD separado no Brasil). */
export const phoneValueFromParts = (ddi?: string | null, ddd?: string | null, number?: string | null): PhoneValue => {
  const code = ddiOrBrazil(ddi);
  const national = nationalDigits(code, ddd, number);
  if (code !== BRAZIL_DDI) return { ddi: code, ddd: "", number: national };
  const n = stripTypedBrazilDdi(national);
  return n.length === 10 || n.length === 11
    ? { ddi: BRAZIL_DDI, ddd: n.slice(0, 2), number: n.slice(2) }
    : { ddi: BRAZIL_DDI, ddd: "", number: n };
};

/** Dígitos E.164 (ex.: WhatsApp do lead) → valor do formulário. */
export const phoneValueFromE164 = (digits?: string | null): PhoneValue => {
  const d = onlyDigits(digits);
  const code = callingCodeOf(d);
  return code ? phoneValueFromParts(code, null, d.slice(code.length)) : { ...EMPTY_PHONE };
};

/**
 * Texto digitado livre: com "+" (ou "00") o DDI sai do próprio número; sem isso é brasileiro.
 * Nulo quando não há como separar o DDI.
 */
export const parsePhoneInput = (raw: string): PhoneValue | null => {
  const text = raw.trim();
  let digits = onlyDigits(text);
  if (!digits) return null;
  if (!text.startsWith("+") && !text.startsWith("00")) return phoneValueFromParts(BRAZIL_DDI, null, digits);
  if (!text.startsWith("+")) digits = digits.slice(2);
  const code = callingCodeOf(digits);
  return code ? phoneValueFromParts(code, null, digits.slice(code.length)) : null;
};

/** Número nacional para a API (DDD + número no Brasil, o número inteiro fora); nulo sem número. */
export const phoneNational = (value: PhoneValue): string | null => {
  const number = onlyDigits(value.number);
  return number ? onlyDigits(value.ddd) + number : null;
};

export const PHONE_INCOMPLETE = "Telefone incompleto: confira o DDD e o número.";

/** Número completo para o país (Brasil: DDD + 8 ou 9 dígitos). */
export const isPhoneComplete = (value: PhoneValue): boolean => toE164Digits(value.ddi, value.ddd, value.number) !== null;

/** Contato do lead do CRM (mesma leitura do ConvertLeadToPatient do servidor). */
export const leadPhoneValue = (whatsapp?: string | null, phone?: string | null): PhoneValue => {
  if (onlyDigits(whatsapp)) return phoneValueFromE164(whatsapp);
  const digits = onlyDigits(phone);
  if (digits.length >= 12) return phoneValueFromE164(digits);
  return parsePhoneInput(phone ?? "") ?? { ...EMPTY_PHONE };
};
